/**
 * Tiny, safe markdown subset for the texts Flavia writes: paragraphs, "- " /
 * "1. " lists, "### " sub-headings, **bold**, _italic_, `code`, [links](url)
 * and bare http(s) URLs. It parses to a plain tree rendered as React elements
 * (MiniMarkdown), never to HTML, so any markup in the input stays text.
 */

export type Inline =
  | { type: "text"; text: string }
  | { type: "strong"; children: Inline[] }
  | { type: "em"; children: Inline[] }
  | { type: "code"; text: string }
  | { type: "link"; href: string; children: Inline[] };

export type Block =
  | { type: "paragraph"; children: Inline[] }
  | { type: "heading"; children: Inline[] }
  | { type: "list"; ordered: boolean; items: Inline[][] };

const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

/** Only absolute http(s) and mailto links are followed. */
export const safeHref = (href: string): string | null => {
  const trimmed = href.trim();
  return SAFE_HREF.test(trimmed) ? trimmed : null;
};

const INLINE_RX =
  /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)|`([^`]+)`|(?<![\p{L}\p{N}_])_(.+?)_(?![\p{L}\p{N}_])|(?<![*\p{L}\p{N}])\*(?!\s)([^*]+?)\*(?![*\p{L}\p{N}])|(https?:\/\/[^\s<>()]*[^\s<>().,;:!?'"»)])/gu;

export const parseInline = (text: string): Inline[] => {
  const nodes: Inline[] = [];
  let cursor = 0;
  const pushText = (value: string) => {
    if (!value) return;
    const last = nodes[nodes.length - 1];
    if (last?.type === "text") last.text += value;
    else nodes.push({ type: "text", text: value });
  };

  for (const match of text.matchAll(INLINE_RX)) {
    const index = match.index ?? 0;
    pushText(text.slice(cursor, index));
    cursor = index + match[0].length;
    const [raw, strong, linkText, linkHref, code, emUnderscore, emStar, url] =
      match;

    if (strong !== undefined) {
      nodes.push({ type: "strong", children: parseInline(strong) });
    } else if (linkText !== undefined) {
      const href = safeHref(linkHref);
      if (href)
        nodes.push({ type: "link", href, children: parseInline(linkText) });
      else pushText(linkText);
    } else if (code !== undefined) {
      nodes.push({ type: "code", text: code });
    } else if (emUnderscore !== undefined || emStar !== undefined) {
      nodes.push({
        type: "em",
        children: parseInline(emUnderscore ?? emStar),
      });
    } else if (url !== undefined) {
      nodes.push({
        type: "link",
        href: url,
        children: [{ type: "text", text: url }],
      });
    } else {
      pushText(raw);
    }
  }
  pushText(text.slice(cursor));
  return nodes;
};

const BULLET_RX = /^\s*[-*•]\s+(.*)$/;
const ORDERED_RX = /^\s*\d+[.)]\s+(.*)$/;
const HEADING_RX = /^\s*#{1,6}\s+(.*)$/;

export const parseMarkdown = (source: string | null | undefined): Block[] => {
  if (!source) return [];
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({
        type: "paragraph",
        children: parseInline(paragraph.join(" ")),
      });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push({
        type: "list",
        ordered: list.ordered,
        items: list.items.map(parseInline),
      });
      list = null;
    }
  };

  for (const rawLine of source.replace(/\r\n?/g, "\n").split("\n")) {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }
    const heading = HEADING_RX.exec(line);
    const bullet = BULLET_RX.exec(line);
    const ordered = bullet ? null : ORDERED_RX.exec(line);

    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ type: "heading", children: parseInline(heading[1]) });
    } else if (bullet || ordered) {
      flushParagraph();
      const isOrdered = !!ordered;
      if (list && list.ordered !== isOrdered) flushList();
      if (!list) list = { ordered: isOrdered, items: [] };
      list.items.push((bullet ?? ordered)![1].trim());
    } else if (list && /^\s{2,}\S/.test(rawLine)) {
      // Indented continuation of the previous list item.
      list.items[list.items.length - 1] += ` ${line.trim()}`;
    } else {
      flushList();
      paragraph.push(line.trim());
    }
  }
  flushParagraph();
  flushList();
  return blocks;
};
